import classnames from "classnames"
import { AnimatePresence, motion } from "framer-motion"
import isFunction from "lodash/isFunction"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import Fullscreen, { FULLSCREEN_LAYER_OVERLAY } from "../fullscreen/Fullscreen"
import useControlledState from "../hooks/useControlledState"
import useWindowSize from "../hooks/useWindowSize"
import { useObserveSizeContext } from "../observers/ObserveSizeContext"
import ScrollView from "../scroll/ScrollView"
import Screensize from "../ui/Screensize"
import "./DockPanel.css"
import DockPanelHeader from "./DockPanelHeader"

// Account for alert banner offset.
const StyledDockPanel = styled(motion.div)`
  padding-top: ${(p) => 50 + p.$offsetTop}px;
`
const StyledDockPanelHeader = styled.div`
  padding-top: ${(p) => p.$offsetTop}px;
`

export const DockPanelContext = React.createContext()

const DockPanel = ({
  title,
  link,
  children,
  onChangeLayout,
  isOpen: isOpenProp,
  onWillClose,
  onClose,
  maxWidth,
  maxHeight,
  closeRoute,
  allowOrient,
  HeaderComponent,
  defaultDocked,
  defaultBottom,
  dockOffset,
  panelWidthPerc,
  panelHeightPerc,
  className: klassName
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [isOpen, setIsOpen] = useControlledState(isOpenProp || undefined)
  const [isDocked, setIsDocked] = useState(defaultDocked)
  const [orientBottom, setOrientBottom] = useState(defaultBottom)
  const [header, setHeader] = useState({
    title,
    link
  })
  const { getRectValue } = useObserveSizeContext()
  // Only account for aleart banner offset when oriented top.
  const offsetTop = orientBottom ? 0 : getRectValue("alert.height")

  // Determine if the DockPanel isOpen state is controlled.
  const isOpenControlled = isOpenProp !== undefined

  // We default isOpen to undefined so we can avoid an initial render of the panel,
  // deferring until component opens for the first time. This helps avoid a quick flash
  // of the panel transitioning to the closed position on mount, which is sometimes visible
  // on initial page load.
  const isPanelEnabled = isOpen !== undefined

  useEffect(() => {
    onChangeLayout &&
      onChangeLayout({
        open: isOpen,
        docked: isDocked,
        orientation: orientBottom ? "bottom" : "side"
      })
  }, [isOpen, isDocked, orientBottom])

  // ensure DockPanel rerenders whenever screensize changes since
  // the width and height of the panel are set absolutely based on screensize
  const windowSize = useWindowSize()

  useEffect(() => {
    const nextIsMobile = Screensize.Provider.getScreensize() === "sm"
    if (nextIsMobile !== isMobile) {
      setIsMobile(nextIsMobile)
    }
  }, [windowSize])

  useEffect(() => {
    // Only handle state as result of children changing
    // if DockPanel isOpen is not controlled.
    if (!isOpenControlled) {
      // Automatically open when new children are set
      if (children) {
        setIsDocked(false)
        setIsOpen(true)
      }

      // The panel has been closed via some other method than clicking the close button, so let's attempt to execute any closing cleanup logic
      if (!children && isOpen) {
        handleOnWillClose()
        setIsOpen(false)
      }
    }
  }, [children])

  const handleOnWillClose = () => {
    isFunction(onWillClose) && onWillClose()
  }
  const toggleBottom = () => setOrientBottom(!orientBottom)
  const toggleDocked = () => setIsDocked(!isDocked)
  const handleClose = () => {
    setIsOpen(false)
    handleOnWillClose()
  }
  const updateHeader = ({ title, link }) => {
    setHeader({
      title,
      link
    })
  }

  // Update header when title or link changes
  useEffect(() => {
    updateHeader({
      title,
      link
    })
  }, [title, link])

  // Calculate panel dimensions and transform for animation
  const getPanelLayout = (offscreen = false, docked = isDocked) => {
    const widthPerc = orientBottom || isMobile ? 1 : panelWidthPerc
    const heightPerc = isMobile || !orientBottom ? 1 : panelHeightPerc

    let panelWidth = window.innerWidth * widthPerc
    let panelHeight = window.innerHeight * heightPerc

    if (!orientBottom && panelWidth > maxWidth) {
      panelWidth = maxWidth
    }
    if (orientBottom && panelHeight > maxHeight) {
      panelHeight = maxHeight
    }

    let x = 0,
      y = 0
    if (docked) {
      if (orientBottom) {
        y = panelHeight - dockOffset
      } else {
        x = panelWidth - dockOffset
      }
    }
    if (offscreen) {
      if (orientBottom) {
        y = panelHeight
      } else {
        x = panelWidth
      }
    }
    return { width: panelWidth, height: panelHeight, x, y }
  }

  const className = classnames(
    "DockPanel",
    {
      "DockPanel--open": isOpen,
      "DockPanel--docked": isDocked,
      "DockPanel--mobile": isMobile,
      "DockPanel--bottom": orientBottom
    },
    klassName
  )

  const transition = {
    type: "spring",
    stiffness: 220,
    damping: 25
  }

  // Framer Motion variants for panel animation
  const panelVariants = {
    open: () => {
      const { width, height, x, y } = getPanelLayout(false, isDocked)
      return {
        width,
        height,
        x,
        y,
        transition
      }
    },
    closed: () => {
      const { width, height, x, y } = getPanelLayout(true, isDocked)
      return {
        width,
        height,
        x,
        y,
        transition
      }
    }
  }

  const hasBackground = !isDocked && !!isOpen

  return (
    <DockPanelContext.Provider
      value={{
        setHeader: updateHeader
      }}
    >
      <Fullscreen
        layer={FULLSCREEN_LAYER_OVERLAY}
        background={hasBackground ? { overlay: "rgba(0, 0, 0, 0.3)" } : null}
        onClickBackground={!isDocked && toggleDocked}
        allowScroll={hasBackground}
        offset={{ top: offsetTop }}
      >
        <AnimatePresence onExitComplete={onClose}>
          {isPanelEnabled && isOpen && (
            <StyledDockPanel
              className={className}
              $offsetTop={offsetTop}
              initial="closed"
              animate="open"
              exit="closed"
              variants={panelVariants}
            >
              <StyledDockPanelHeader
                className="DockPanel__header"
                $offsetTop={offsetTop}
              >
                <DockPanelHeader
                  isDocked={isDocked}
                  isBottom={orientBottom}
                  onDock={toggleDocked}
                  onClose={handleClose}
                  closeRoute={closeRoute}
                  onOrientation={toggleBottom}
                  allowOrient={allowOrient}
                  HeaderComponent={HeaderComponent}
                  title={header.title}
                  link={header.link}
                />
              </StyledDockPanelHeader>
              <ScrollView className="DockPanel__content">{children}</ScrollView>
              {isDocked && (
                <div className="DockPanel__undock" onClick={toggleDocked} />
              )}
            </StyledDockPanel>
          )}
        </AnimatePresence>
      </Fullscreen>
    </DockPanelContext.Provider>
  )
}

DockPanel.displayName = "DockPanel"

DockPanel.propTypes = {
  // default docked state when children provided
  defaultDocked: PropTypes.bool,
  // default orientation when children provided
  defaultBottom: PropTypes.bool,
  // % width when in side orientation
  panelWidthPerc: PropTypes.number,
  // % height when in bottom orientation
  panelHeightPerc: PropTypes.number,
  // px to reveal when panel is docked
  dockOffset: PropTypes.number,
  onChangeLayout: PropTypes.func,
  onWillClose: PropTypes.func,
  onClose: PropTypes.func,
  closeRoute: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  renderContentOnTransition: PropTypes.bool,
  HeaderComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  link: PropTypes.string
}

DockPanel.defaultProps = {
  defaultDocked: false,
  defaultActive: true,
  defaultBottom: false,
  allowOrient: true,
  panelWidthPerc: 0.5, // .85
  panelHeightPerc: 0.5, // .4
  maxWidth: 1000,
  maxHeight: 500,
  dockOffset: 50,
  renderContentOnTransition: true
}

export default DockPanel
